import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  approvalRequestsApi,
  type ApprovalRequest,
  type ApprovalRequestDecision,
  type ApprovalRequestStatus,
  type CreateApprovalRequestInput,
} from '../api/approvalRequests';
import { useToast } from '../components/ui/Toast';
import {
  PENDING_COUNT_POLL_MS,
  effectiveStatus,
  listPollInterval,
  pollInterval,
} from '../pages/approvalRequests.logic';
import { readyToPublishCount } from '../pages/contentRevisions.logic';

export const approvalRequestKeys = {
  all: ['approvalRequests'] as const,
  list: (status?: ApprovalRequestStatus) =>
    [...approvalRequestKeys.all, 'list', status ?? 'all'] as const,
  detail: (id: string) => [...approvalRequestKeys.all, 'detail', id] as const,
};

/**
 * Every open request in the console. Polls only while something in the list
 * can still change, so an idle Approvals tab costs nothing.
 */
export function useApprovalRequests(status?: ApprovalRequestStatus) {
  return useQuery({
    queryKey: approvalRequestKeys.list(status),
    queryFn: () => approvalRequestsApi.list(status),
    refetchInterval: query => listPollInterval(query.state.data, new Date().toISOString()),
  });
}

/** One request, polled while it is still moving; this is what the requester's modal watches. */
export function useApprovalRequest(id: string | null) {
  return useQuery({
    queryKey: approvalRequestKeys.detail(id ?? ''),
    queryFn: () => approvalRequestsApi.get(id ?? ''),
    enabled: id !== null,
    refetchInterval: query => {
      const request = query.state.data;
      return pollInterval(request ? effectiveStatus(request, new Date().toISOString()) : undefined);
    },
  });
}

/** Sidebar badge: how many requests are waiting for a second operator right now. */
export function usePendingApprovalRequestCount(enabled = true) {
  return useQuery({
    queryKey: [...approvalRequestKeys.list('PENDING'), 'count'],
    queryFn: () => approvalRequestsApi.list('PENDING'),
    select: (requests: ApprovalRequest[]) => {
      const now = new Date().toISOString();
      return requests.filter(request => effectiveStatus(request, now) === 'PENDING').length;
    },
    refetchInterval: PENDING_COUNT_POLL_MS,
    enabled,
  });
}

function useInvalidateApprovalRequests() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: approvalRequestKeys.all });
}

export function useCreateApprovalRequest() {
  const invalidate = useInvalidateApprovalRequests();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: CreateApprovalRequestInput) => approvalRequestsApi.create(input),
    onSuccess: () => {
      invalidate();
      toast.success(
        'Approval requested. A second operator can now decide it on the Approvals page.'
      );
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/**
 * DM-7214: open a request through a caller-supplied server call (proposing a
 * content revision stores the edit and opens its request in one step). The
 * content list is refreshed too, because the item now carries the revision.
 */
export function useOpenApprovalRequestWith(
  open: ((reason: string, note: string | undefined) => Promise<ApprovalRequest>) | undefined
) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateApprovalRequests();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ reason, note }: { reason: string; note: string | undefined }) => {
      if (!open) return Promise.reject(new Error('This action cannot open a request here.'));
      return open(reason, note);
    },
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['content'] });
      toast.success(
        'Revision stored on the server and sent for approval. You can close this page; nothing is lost.'
      );
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/** DM-7214: approved revisions of mine that still wait on me (sidebar badge). */
export function useReadyToPublishCount(viewerSub: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...approvalRequestKeys.list('APPROVED'), 'mine', viewerSub ?? ''],
    queryFn: () => approvalRequestsApi.list('APPROVED'),
    select: (requests: ApprovalRequest[]) =>
      readyToPublishCount(requests, viewerSub, new Date().toISOString()),
    refetchInterval: PENDING_COUNT_POLL_MS,
    enabled: enabled && viewerSub !== undefined,
  });
}

export function useDecideApprovalRequest() {
  const invalidate = useInvalidateApprovalRequests();
  const toast = useToast();
  return useMutation({
    mutationFn: ({
      id,
      decision,
      note,
    }: {
      id: string;
      decision: ApprovalRequestDecision;
      note?: string;
    }) => approvalRequestsApi.decide(id, decision, note),
    onSuccess: (_data, { decision }) => {
      invalidate();
      toast.success(
        decision === 'approved'
          ? 'Request approved. The requester can complete it from their console.'
          : 'Request rejected.'
      );
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

export function useWithdrawApprovalRequest() {
  const invalidate = useInvalidateApprovalRequests();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => approvalRequestsApi.withdraw(id),
    onSuccess: () => {
      invalidate();
      toast.success('Request withdrawn.');
    },
    onError: (error: Error) => toast.error(error.message),
  });
}
